import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Lock } from 'lucide-react';
import { pricingTiers } from '../mock';
import { startCheckout } from '../lib/checkout';
import { usePricingEnabled } from '../lib/usePricingEnabled';
import TrustBadges from './TrustBadges';

const PricingCard = ({ tier, billing, pricingEnabled }) => {
  const displayPrice = billing === 'annual' ? Math.round(tier.price * 0.8) : tier.price;
  return (
    <div className={`relative flex flex-col rounded-2xl p-6 md:p-7 border transition-all ${
      tier.highlight
        ? 'bg-stone-900 text-white border-stone-900 shadow-2xl md:-translate-y-4'
        : 'bg-white text-stone-900 border-stone-200 hover:shadow-lg'
    }`}>
      {tier.badge && (
        <span className={`absolute -top-3 left-6 text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full ${
          tier.highlight ? 'bg-[#F97316] text-white' : 'bg-[#4F46E5] text-white'
        }`}>
          {tier.badge}
        </span>
      )}
      <h3 className="text-[22px] font-extrabold" style={{ fontFamily: 'Plus Jakarta Sans' }}>{tier.name}</h3>
      <p className={`text-[13px] mt-1 ${tier.highlight ? 'text-stone-300' : 'text-stone-500'}`}>{tier.tagline}</p>

      <div className="mt-5 flex items-baseline gap-1">
        <span className="text-[42px] font-extrabold" style={{ fontFamily: 'Plus Jakarta Sans' }}>${displayPrice}</span>
        <span className={`text-[13px] ${tier.highlight ? 'text-stone-300' : 'text-stone-500'}`}>/month</span>
      </div>
      {billing === 'annual' && (
        <p className={`text-[11px] mt-1 ${tier.highlight ? 'text-emerald-400' : 'text-emerald-600'} font-semibold`}>
          Billed annually • 20% off
        </p>
      )}

      <button
        data-testid={`pricing-preview-cta-${tier.name.toLowerCase()}`}
        onClick={() => pricingEnabled && startCheckout(tier.name)}
        disabled={!pricingEnabled}
        className={`mt-6 px-5 py-3 rounded-lg text-[13px] font-bold transition inline-flex items-center justify-center gap-2 ${
          !pricingEnabled
            ? 'bg-stone-200 text-stone-500 cursor-not-allowed'
            : tier.highlight
              ? 'bg-[#F97316] text-white hover:bg-[#EA580C]'
              : 'bg-stone-900 text-white hover:bg-stone-800'
        }`}
      >
        {pricingEnabled ? (
          <>{tier.cta} <ArrowRight size={14} /></>
        ) : (
          <>Coming soon <Lock size={13} /></>
        )}
      </button>

      <ul className="mt-6 space-y-2.5">
        {tier.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-[13px]">
            <Check size={15} className={`mt-0.5 flex-shrink-0 ${tier.highlight ? 'text-[#F97316]' : 'text-emerald-600'}`} strokeWidth={2.5} />
            <span className={tier.highlight ? 'text-stone-200' : 'text-stone-700'}>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PricingPreview = () => {
  const [billing, setBilling] = useState('monthly');
  const pricingEnabled = usePricingEnabled();
  return (
    <section id="pricing" className="py-20 md:py-28">
      <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-4">
          <p className="text-[12px] font-bold uppercase tracking-widest text-[#F97316] mb-3">Pricing</p>
          <h2 className="text-[34px] md:text-[46px] font-extrabold text-stone-900 leading-tight">
            Simple pricing. Roughly 30% cheaper than the alternative.
          </h2>
          <p className="mt-4 text-[16px] text-stone-600">
            Every plan is month-to-month. Cancel from your dashboard whenever you like.
          </p>
          {pricingEnabled === false && (
            <div data-testid="pricing-beta-banner" className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4F46E5]/10 border border-[#4F46E5]/30 text-[13px] font-semibold text-[#4F46E5]">
              <Lock size={13} /> We're in Beta — sign-ups open soon. Grab your free heatmap above to get on the list.
            </div>
          )}
        </div>

        {/* Billing toggle */}
        <div className="flex justify-center mt-8 mb-12">
          <div className="inline-flex p-1 bg-white rounded-full border border-stone-200 shadow-sm">
            {['monthly', 'annual'].map((opt) => (
              <button
                key={opt}
                onClick={() => setBilling(opt)}
                className={`px-5 py-2 text-[13px] font-bold rounded-full transition ${
                  billing === opt ? 'bg-stone-900 text-white' : 'text-stone-600 hover:text-stone-900'
                }`}
              >
                {opt === 'monthly' ? 'Monthly' : 'Annual – save 20%'}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pricingTiers.map((t) => <PricingCard key={t.name} tier={t} billing={billing} pricingEnabled={pricingEnabled} />)}
        </div>

        <div className="mt-12">
          <TrustBadges compact />
        </div>

        <div className="text-center mt-10">
          <Link to="/pricing" className="inline-flex items-center gap-2 text-[14px] font-bold text-[#4F46E5] hover:text-[#4338CA]">
            See the full comparison <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PricingPreview;
